import { useTranslation } from "react-i18next";
import { Alert, Anchor, Grid, Paper, Stack, Text, Title } from "@mantine/core";
import { useLink } from "../router";
import { Header } from "./Header";
import { Shell } from "./Shell";

/**
 * Where an SSO round trip lands when it did not end in a session.
 *
 * The server can only redirect -- the callback is a browser navigation, not a fetch -- so it
 * sends a stable code and this turns it into a sentence. `linked` is the success case: a
 * signed-in person attaching a provider rather than signing in with one.
 */
const CODES = [
  "failed",
  "unverified",
  "disabled",
  "maintenance",
  "inactive",
  "conflict",
  "linked",
] as const;

export function SsoPage() {
  const { t } = useTranslation();
  const home = useLink("/");
  const raw = new URLSearchParams(window.location.search).get("error") ?? "failed";
  // Never rendered from the querystring itself: an unknown code must not become a message.
  const code = (CODES as readonly string[]).includes(raw) ? raw : "failed";
  const ok = code === "linked";

  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main style={{ padding: 20 }}>
        <Grid columns={100}>
          <Grid.Col span={{ base: 100, sm: 80, md: 55 }}>
            <Paper p="md" m={{ base: 0, sm: 20 }} style={{ overflowY: "auto", minHeight: 0 }}>
              <Title order={2} fz="lg" lh={1.5} mb={6}>
                {t("ssoPage.heading")}
              </Title>
              <Stack gap="sm">
                {ok ? (
                  <Text>{t("ssoPage.linked")}</Text>
                ) : (
                  <Alert color="red" variant="light">
                    {t(`ssoPage.${code}`)}
                  </Alert>
                )}
                <Anchor {...home}>{t("confirmPage.goToArchive")}</Anchor>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Shell.Main>
    </Shell>
  );
}
